import React, { useMemo } from 'react';
import type { ColorResult } from 'react-color';
import {
    EditableInput,
    Hue,
    Saturation
} from 'react-color/lib/components/common';
import styled from 'styled-components';

import { visuallyHidden } from '../../../styles/mixins';
import ColorCircleBlock from '../ColorCircleBlock';
import { StringToColorResult } from '../useColorPickerConverter';
import { vars } from '../../../theme';

interface ColorPickerDrawerControlsProps {
    onColorTextChange: (data: ColorResult) => void;
    selectedColor: StringToColorResult;
    onColorChange?: (color: any) => void;
    className?: string;
}

const HBColorPickerDrawerControls = ({
    selectedColor,
    onColorTextChange,
    onColorChange,
    className
}: ColorPickerDrawerControlsProps) => {
    const pointerStyle = useMemo(
        () => ({
            pointer: {
                position: 'absolute',
                top: `${100 - selectedColor.hsv.v}%`,
                left: `${selectedColor.hsv.s}%`,
                cursor: 'pointer',
                transform: 'translate(-50%, -50%)'
            }
        }),
        [selectedColor.hsv.v, selectedColor.hsv.s]
    );

    return (
        <InDrawerContainer className={className}>
            <InDrawerSaturationContainer>
                <Saturation
                    color={selectedColor.hsl}
                    // @ts-ignore
                    hsl={selectedColor.hsl}
                    pointer={InDrawerKnob}
                    style={pointerStyle}
                    hsv={selectedColor.hsv}
                    onChange={onColorChange}
                />
            </InDrawerSaturationContainer>
            <InDrawerColorControls>
                <InDrawerHueContainer aria-label="Color hue slider">
                    <Hue
                        {...selectedColor}
                        onChange={onColorChange}
                        pointer={InDrawerKnobHue}
                    />
                </InDrawerHueContainer>
                <InDrawerSwatchContainer>
                    <ColorCircleBlock
                        isSquare
                        color={selectedColor?.rgb}
                        alpha={selectedColor?.rgb?.a}
                        testId="color-picker-drawer-swatch"
                    />
                    <InDrawerInputContainer>
                        <EditableInput
                            onChange={onColorTextChange}
                            value={selectedColor?.hex}
                            label="Color value"
                        />
                    </InDrawerInputContainer>
                </InDrawerSwatchContainer>
            </InDrawerColorControls>
        </InDrawerContainer>
    );
};

export default HBColorPickerDrawerControls;

// -------------------------------- Styles -------------------------------- //
const InDrawerContainer = styled.div`
    width: 100%;
    border-radius: ${vars.radius.xs};
`;

const InDrawerKnob = styled.div`
    display: flex;
    width: 32px;
    height: 32px;
    border: 1px solid ${vars.colors.stroke.secondary};
    border-radius: ${vars.radius.full};
    background-color: ${vars.colors.surface.item};
    box-shadow: ${vars.shadow['20']};
`;

const InDrawerSwatchContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
    height: 100%;
`;

const InDrawerColorControls = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    // react-color overrides
    .hue-horizontal {
        border-radius: ${vars.radius.full};
    }
`;

const InDrawerSaturationContainer = styled.div`
    position: relative;
    height: 112px;

    // react-color overrides
    .vaul-scrollable,
    .saturation-black {
        border-radius: ${vars.radius.sm};
    }
`;

const InDrawerHueContainer = styled.div`
    position: relative;
    width: 100%;
    height: 20px;
    margin: 24px 0;
`;

const InDrawerKnobHue = styled(InDrawerKnob)`
    transform: translateY(-5px);
`;

const InDrawerInputContainer = styled.div`
    position: relative;
    width: 100%;
    margin: 0;

    input {
        width: 100%;
        height: 44px;
        border-radius: ${vars.radius.xs};
        padding: 11px 16px;
        color: ${vars.colors.text.secondary};
        background-color: ${vars.colors.surface.itemInset};
        font-family: ${vars.fonts.body};
        border: none;
    }

    // visually hidden but accessible to screen readers
    label {
        ${visuallyHidden}
    }
`;
